'use client';

import React, { createContext, useContext, useEffect, useRef, useState } from 'react';

import { config } from '@/config';
import { createEcho, type EchoInstance } from '@/lib/v2/echo';

interface EchoContextValue {
  echo: EchoInstance | null;
}

export const EchoContext = createContext<EchoContextValue>({ echo: null });

export const useEcho = () => useContext(EchoContext);

interface EchoProviderProps {
  children: React.ReactNode;
  /** Bearer token for private channel auth. Pass null/undefined when unauthenticated. */
  token: string | null | undefined;
}

export function EchoProvider({ children, token }: EchoProviderProps): React.JSX.Element {
  const echoRef = useRef<EchoInstance | null>(null);
  const [echo, setEcho] = useState<EchoInstance | null>(null);

  useEffect(() => {
    if (!config.reverb.key) {
      console.warn('[EchoProvider] VITE_REVERB_APP_KEY not set — real-time disabled');
      return;
    }

    if (!token) {
      console.log('[EchoProvider] No auth token — skipping Echo setup');
      return;
    }

    // Tear down any previous connection (e.g. token rotated)
    if (echoRef.current) {
      console.log('[EchoProvider] Disconnecting previous Echo instance');
      echoRef.current.disconnect();
      echoRef.current = null;
    }

    const instance = createEcho(token);
    echoRef.current = instance;
    setEcho(instance);

    return () => {
      console.log('[EchoProvider] Cleanup — disconnecting Echo');
      instance.disconnect();
      echoRef.current = null;
      setEcho(null);
    };
  }, [token]);

  return <EchoContext.Provider value={{ echo }}>{children}</EchoContext.Provider>;
}
